import React, { useEffect, useState } from 'react';
import { DelayType, ResponseDelay } from 'types/rest/mockServiceTypes';
import i18n from 'i18n';
import RadioButton from 'components/buttons/RadioButton';
import { FormGroup } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import { useFormInput, Validator, ValidatorType } from 'hooks/useFormInput';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import _ from 'lodash';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { convertToNumber } from 'utils/commonUtil';
import { useIsMount } from 'hooks/useIsMountHook';

type ThinkTimeRadioGroupProps = {
  responseDelay?: ResponseDelay;
  updateResponseDelay: (value: ResponseDelay) => void;
  updateState?: (field: string, value: any) => void;
  noDelayText?: string;
  disabled?: boolean;
};

const ThinkTimeRadioGroup = (props: ThinkTimeRadioGroupProps) => {
  const isMount = useIsMount();
  const [delayType, setDelayType] = useState(
    props.responseDelay && props.responseDelay.type ? props.responseDelay.type : DelayType.NONE,
  );
  const [duration, setDuration] = useState<number>(
    props.responseDelay && props.responseDelay.duration ? props.responseDelay.duration : 1,
  );
  const [upper, setUpper] = useState<number>(
    props.responseDelay && props.responseDelay.upper ? props.responseDelay.upper : 1000,
  );
  const [lower, setLower] = useState<number>(
    props.responseDelay && props.responseDelay.lower ? props.responseDelay.lower : 100,
  );
  const [median, setMedian] = useState<number>(
    props.responseDelay && props.responseDelay.median ? props.responseDelay.median : 500,
  );
  const [sigma, setSigma] = useState<number>(
    props.responseDelay && props.responseDelay.sigma ? props.responseDelay.sigma : 0,
  );

  /** Base validators for all inputs */
  const validators: Validator[] = [
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('mockServices:parametersTab.thinkTimeValidation.required'),
    },
    {
      type: ValidatorType.INTEGER,
      invalidMessage: i18n.t('mockServices:parametersTab.thinkTimeValidation.numberValidation'),
    },
  ];

  /**
   * Updates type of the delay with radio buttons.
   * @param type
   */
  const updateDelayType = (type: DelayType) => {
    setDelayType(type);
  };

  useEffect(() => {
    if (!isMount) {
      propagateResponseDelay();
    }
  }, [delayType]);

  useEffect(() => {
    if (!isMount) {
      propagateResponseDelayDebounced();
    }
  }, [duration, sigma, median]);

  useEffect(() => {
    if (!isMount) {
      validateUniformDelay('lower');
    }
  }, [lower]);

  useEffect(() => {
    if (!isMount) {
      validateUniformDelay('upper');
    }
  }, [upper]);

  const validateUniformDelay = (type: 'lower' | 'upper') => {
    //Cross field validation fails
    if (lower > upper) {
      const validation = {
        invalidMessage: i18n.t('mockServices:parametersTab.thinkTimeValidation.lowerUpperValidation'),
        isValid: false,
      };
      if (type === 'lower') {
        uniformLowerInput.overrideValidation(validation);
      } else {
        uniformUpperInput.overrideValidation(validation);
      }
    }
    // Both values are valid, we can propagate them and reset validation
    else {
      const validation = { invalidMessage: '', isValid: true };
      uniformLowerInput.overrideValidation(validation);
      uniformUpperInput.overrideValidation(validation);
      propagateResponseDelayDebounced();
    }
  };

  // Values are propagated up thru useEffect hooks
  const durationInput = useFormInput(
    duration,
    fn => {
      if (fn.isValid) {
        setDuration(convertToNumber(fn.value));
      } else {
        if (props.updateState !== undefined) {
          props.updateState('isValid', false);
        }
      }
    },
    ...[
      ...validators,
      {
        type: ValidatorType.MAX,
        value: 999999999,
        invalidMessage: i18n.t('mockServices:parametersTab.thinkTimeValidation.numberValidationRange'),
      },
    ],
  );

  const uniformLowerInput = useFormInput(
    lower,
    fn => {
      if (fn.isValid) {
        setLower(convertToNumber(fn.value));
      } else {
        if (props.updateState !== undefined) {
          props.updateState('isValid', false);
        }
      }
    },
    ...validators,
  );

  const uniformUpperInput = useFormInput(
    upper,
    fn => {
      if (fn.isValid) {
        setUpper(convertToNumber(fn.value));
      } else {
        if (props.updateState !== undefined) {
          props.updateState('isValid', false);
        }
      }
    },
    ...validators,
  );

  const logNormalMedianInput = useFormInput(
    median,
    fn => {
      if (fn.isValid) {
        setMedian(convertToNumber(fn.value));
      } else {
        if (props.updateState !== undefined) {
          props.updateState('isValid', false);
        }
      }
    },
    ...validators,
  );

  const logNormalSigmaInput = useFormInput(
    sigma,
    fn => {
      if (fn.isValid) {
        setSigma(convertToNumber(fn.value));
      } else {
        if (props.updateState !== undefined) {
          props.updateState('isValid', false);
        }
      }
    },
    ...validators,
  );

  /**
   * Creates response delay from local state.
   */
  const propagateResponseDelay = () => {
    props.updateResponseDelay({
      lower: lower,
      upper: upper,
      duration: duration,
      type: delayType,
      chunks: 0,
      dribbled: false,
      median: median,
      sigma: sigma,
    });
  };

  const propagateResponseDelayDebounced = _.debounce(propagateResponseDelay, 600);

  const gapBetweenRadios = 'mb-3';
  const gapInExpandedDiv = 'mb-3';

  return (
    <FormGroup id={'think-time'} className={'ml-1'}>
      {/* No Delay */}
      <div className={gapInExpandedDiv}>
        <RadioButton
          disabled={props.disabled}
          isChecked={delayType === DelayType.NONE}
          id={'noDelay'}
          label={props.noDelayText ? props.noDelayText : i18n.t('common:thinkTime.noDelay')}
          onChange={() => updateDelayType(DelayType.NONE)}
        />
      </div>
      {/* No Delay */}

      {/* Fixed Delay */}
      <div className={gapBetweenRadios}>
        <RadioButton
          disabled={props.disabled}
          isChecked={delayType === DelayType.FIXED}
          id={'fixedDelay'}
          label={i18n.t('common:thinkTime.fixedDelay')}
          onChange={() => updateDelayType(DelayType.FIXED)}
        />
        {delayType === DelayType.FIXED && (
          <div className={gapInExpandedDiv}>
            <InputGroup>
              <BzFormControlWithHook
                disabled={props.disabled}
                useFormInputHook={durationInput}
                placeholder={i18n.t('common:thinkTime.placeholders.fixed')}
                required={false}
                hideErrors={true}
              />
              <InputGroup.Append>
                <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
              </InputGroup.Append>
              <div className="invalid-feedback">{durationInput.invalidMessage}</div>
            </InputGroup>
          </div>
        )}
      </div>
      {/* ./ Fixed Delay */}

      {/* Uniform delay */}
      <div className={gapBetweenRadios}>
        <RadioButton
          disabled={props.disabled}
          isChecked={delayType === DelayType.UNIFORM}
          id={'randomUniform'}
          label={i18n.t('common:thinkTime.randomUniform')}
          onChange={() => setDelayType(DelayType.UNIFORM)}
        ></RadioButton>
        {delayType === DelayType.UNIFORM && (
          <Row className={gapInExpandedDiv}>
            <Col>
              <InputGroup>
                <BzFormControlWithHook
                  useFormInputHook={uniformLowerInput}
                  placeholder={i18n.t('common:thinkTime.placeholders.lower')}
                  required={false}
                  hideErrors={true}
                />
                <InputGroup.Append>
                  <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
                </InputGroup.Append>
                <div className="invalid-feedback">{uniformLowerInput.invalidMessage}</div>
              </InputGroup>
              <p
                className="text-muted small"
                dangerouslySetInnerHTML={{ __html: i18n.t('common:thinkTime.uniformLower') }}
              ></p>
            </Col>
            <Col>
              <InputGroup>
                <BzFormControlWithHook
                  id={'upperInput'}
                  useFormInputHook={uniformUpperInput}
                  placeholder={i18n.t('common:thinkTime.placeholders.upper')}
                  required={false}
                  hideErrors={true}
                />
                <InputGroup.Append>
                  <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
                </InputGroup.Append>
                <div className="invalid-feedback">{uniformUpperInput.invalidMessage}</div>
              </InputGroup>
              <p
                className="text-muted small"
                dangerouslySetInnerHTML={{ __html: i18n.t('common:thinkTime.uniformUpper') }}
              ></p>
            </Col>
          </Row>
        )}
      </div>
      {/* ./ Uniform delay */}

      {/* Random Log Normal delay */}
      <div className={gapBetweenRadios}>
        <RadioButton
          disabled={props.disabled}
          isChecked={delayType === DelayType.LOGNORMAL}
          id={'randomLognormal'}
          label={i18n.t('common:thinkTime.randomLognormal')}
          onChange={() => setDelayType(DelayType.LOGNORMAL)}
        ></RadioButton>
        {delayType === DelayType.LOGNORMAL && (
          <Row>
            <Col>
              <InputGroup>
                <BzFormControlWithHook
                  disabled={props.disabled}
                  useFormInputHook={logNormalMedianInput}
                  placeholder={i18n.t('common:thinkTime.placeholders.median')}
                  required={false}
                  hideErrors={true}
                />
                <InputGroup.Append>
                  <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
                </InputGroup.Append>
                <div className="invalid-feedback">{logNormalMedianInput.invalidMessage}</div>
              </InputGroup>
              <p
                className="text-muted small"
                dangerouslySetInnerHTML={{ __html: i18n.t('common:thinkTime.lognormalMedian') }}
              ></p>
            </Col>
            <Col>
              <InputGroup>
                <BzFormControlWithHook
                  disabled={props.disabled}
                  id={'upperInput'}
                  useFormInputHook={logNormalSigmaInput}
                  placeholder={i18n.t('common:thinkTime.placeholders.sigma')}
                  required={false}
                  hideErrors={true}
                />
                <div className="invalid-feedback">{logNormalSigmaInput.invalidMessage}</div>
              </InputGroup>
              <p
                className="text-muted small"
                dangerouslySetInnerHTML={{ __html: i18n.t('common:thinkTime.lognormalSigma') }}
              ></p>
            </Col>
          </Row>
        )}
      </div>
      {/* ./ Random Log Normal delay */}
    </FormGroup>
  );
};

export default ThinkTimeRadioGroup;
